import { Context } from './Context';
import { SelectionObject } from './core-elements/BasicElements';

let _secondarySelectionMatDef = undefined; // cache the secondary selection color
export const setSecondarySelectionMaterial = (secondarySelectionMatDef) => {
    _secondarySelectionMatDef = secondarySelectionMatDef;
};
export const getSecondarySelectionMaterial = () => {
    return _secondarySelectionMatDef;
};

// Array of selection contexts used in a command
/** @type {Map<Context, {context: Context, inFocus: boolean}>} */
let contextList = new Map();

// Set holds the selection objects in the inactive selection contexts
/** @type {Map<string| any[], SelectionObject>} */
let greenObjectMap = new Map();

// This only needs to be done for the snap point related objects because of the way they are displayed.
/**
 *
 * @param {Context} context
 * @param {boolean} inActiveContext
 */
const setActiveContextToSnapPoints = (context, inActiveContext) => {
    if (context) {
        Array.from(context.getSelected()).forEach((/** @type {import('./SnapPointBaseElement').SnapPointSelectionObject} */ obj) => {
            if (inActiveContext) {
                if (obj.sgo) {
                    obj.sgo.inActiveContext = true;
                }
            } else {
                if (obj.sgo && obj.sgo.inActiveContext) {
                    obj.sgo.inActiveContext = false;
                }
            }
        });
    }
};

// Adds or updates the selectionContext based on its inFocus status to the contextList
/**
 *
 * @param {object} viewer
 * @param {Context} selContext
 * @param {boolean} inFocus
 */
export const addToContextList = (viewer, selContext, inFocus) => {
    // Check if selContext is already in the set. If found, update its in-focus state if necessary.
    let update = false;
    let contextObj = contextList.get(selContext);
    if (contextObj) {
        if (contextObj.inFocus !== inFocus) {
            contextObj.inFocus = inFocus;
            update = true; // Context focus may be changed
            // Unset inActiveContext flag on selected objects if context is changed to inactive
            if (!inFocus) {
                setActiveContextToSnapPoints(contextObj.context, false);
            }
        } else {
            // context focus not changed. upate only the inactive context
            if (!inFocus) {
                update = true;
            }
        }
    } else {
        // not found, add selContext
        let contextObject = { context: selContext, inFocus: inFocus };
        contextList.set(contextObject.context, contextObject);
        if (!inFocus) {
            update = true;
        }
    }
    if (update) {
        updateSecondarySelectionDisplay(viewer);
    } else {
        const greenObjs = Array.from(greenObjectMap.values());
        setSecondarySelectionColor(viewer, greenObjs, true);
    }
};

// Removes selContext from contextList and update the secondary selection display
/**
 *
 * @param {object} viewer
 * @param {Context} selContext
 */
export const removeFromContextList = (viewer, selContext) => {
    if (!selContext) {
        return;
    }

    if (contextList.delete(selContext)) {
        updateSecondarySelectionDisplay(viewer);
    }
};

// update the green objects based on the context list
const updateGreenObjects = () => {
    greenObjectMap.clear();
    Array.from(contextList.values()).forEach((contextObj) => {
        if (!contextObj.inFocus && contextObj.context.getSecondaryActive()) {
            for (const selObj of contextObj.context) {
                greenObjectMap.set(selObj.id, selObj);
            }
        }
    });
    // set inActiveContext = true to selected snap points in active context
    let activeContext = undefined;
    Array.from(contextList.values()).forEach((contextObj) => {
        if (!activeContext && contextObj.inFocus) {
            activeContext = contextObj.context;
        }
    });
    setActiveContextToSnapPoints(activeContext, true);
};

/**
 *
 * @param viewer
 */
export function clearSecondarySelectionDisplay(viewer) {
    let greenObjs = Array.from(greenObjectMap.values());
    setSecondarySelectionColor(viewer, greenObjs, false);
}

/**
 *
 * @param viewer
 */
export function displaySecondarySelection(viewer) {
    updateGreenObjects();
    let greenObjs = Array.from(greenObjectMap.values());
    setSecondarySelectionColor(viewer, greenObjs, true);
}

// updates the secondary selection color for all selected objects in inactive selection contexts
export const updateSecondarySelectionDisplay = (viewer) => {
    clearSecondarySelectionDisplay(viewer);
    displaySecondarySelection(viewer);
};

/**
 * Sets the secondary selection material for the input selected objects if show === true. Otherwise,
 * reset the material for the selected objects.
 *
 * @param {object} viewer - an instance of viewer manager
 * @param {Array<SelectionObject>} greenObjs - selection objects in the inactive selection contexts
 * @param {boolean} show - true to display secondary selection color; false to reset material
 */
export const setSecondarySelectionColor = (viewer, greenObjs, show) => {
    if (greenObjs.length > 0) {
        // set the color (material) for the selected objects in the context
        let secondaryHighlightPsIds = [];
        for (let obj of greenObjs) {
            if (obj.sourceElement) {
                secondaryHighlightPsIds = obj.sourceElement.secondaryHighlight(obj, show);
                if (secondaryHighlightPsIds && secondaryHighlightPsIds.length > 0) {
                    if (show) {
                        viewer.setMaterialByPsId(secondaryHighlightPsIds, _secondarySelectionMatDef);
                    } else {
                        viewer.resetMaterialByPsId(secondaryHighlightPsIds);
                    }
                }
            }
        }
    }
};
export default {
    setSecondarySelectionMaterial,
    getSecondarySelectionMaterial,
    addToContextList,
    updateSecondarySelectionDisplay,
    clearSecondarySelectionDisplay,
    displaySecondarySelection,
    removeFromContextList,
    setSecondarySelectionColor
};
